import { RemoveMemberSuccessDescription } from "@components/remove-member-success-description";
import { DeleteIcon, Dialog, MenuItem, Text, useToast } from "@flat-ui";
import { useRemoveMemberFromProject } from "@hooks/use-remove-member-from-project";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { UserDisplay } from "@components/common/user-display";
import { useRefreshSeatUsage } from "@components/table/members/assign-seats/use-refresh-seat-usage";
import { fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { removeMemberFromCompany } from "@store/members/members-slice-thunk";
import { selectedSdbCompanyNameSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { TableType, ProjectMemberEvents, WorkspaceMemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";
import { FaroCoreApiUtils } from "@stellar/core-api-client";

/** Determines from where a member should be removed */

/** Menu button for removing a member from a project or workspace */
export function ProjectRemoveMenuButton({
  companyId,
  project: selectedProject,
  member,
  onMenuClose,
  canRemoveFromWorkspace
}) {
  const dispatch = useAppDispatch();
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  const companyName = useAppSelector(selectedSdbCompanyNameSelector);
  const refreshSeatUsage = useRefreshSeatUsage();
  const [removeTarget, setRemoveTarget] = useState(null);
  const {
    removeMember,
    isRemovingMember: isRemovingProjectMember
  } = useRemoveMemberFromProject({
    companyId,
    project: selectedProject,
    trackingProps: {
      dataType: TableType.projectMembers,
      eventLocation: "project-member-menu",
      removeTarget: "project"
    }
  });
  const {
    isRemovingCompanyMember
  } = useAppSelector(fetchingMembersFlagsSelector);
  const isRemoving = removeTarget === "project" ? isRemovingProjectMember : isRemovingCompanyMember;
  function closeDialog() {
    setRemoveTarget(null);
    onMenuClose();
  }
  async function confirmRemove() {
    switch (removeTarget) {
      case "project":
        await removeFromProject();
        break;
      case "workspace":
        await removeFromWorkspace();
        break;
    }
  }
  function trackRemoveMemberEvent(target) {
    trackEvent({
      name: target === "project" ? ProjectMemberEvents.removeMember : WorkspaceMemberEvents.removeMember,
      props: {
        dataType: TableType.projectMembers,
        eventLocation: "project-member-menu",
        removeTarget: target
      }
    });
  }
  async function removeFromProject() {
    try {
      await removeMember(member);
    } finally {
      closeDialog();
    }
  }
  async function removeFromWorkspace() {
    trackRemoveMemberEvent("workspace");
    try {
      await dispatch(removeMemberFromCompany({
        companyId,
        member
      })).unwrap();
      refreshSeatUsage();
      openToast({
        title: "Member removed from workspace",
        message: <RemoveMemberSuccessDescription member={member} subjectType="workspace" entityName={companyName ?? ""} />
      });
    } finally {
      closeDialog();
    }
  }
  const isMemberTeam = FaroCoreApiUtils.Member.isTeam(member);
  const entityLabel = isMemberTeam ? "team" : "member";
  const removeTargetDisplayName = removeTarget === "project" ? "project" : "workspace";
  return <>
      {isMemberTeam || !canRemoveFromWorkspace ? <MenuItem label={"Remove from project"} Icon={DeleteIcon} onClick={() => setRemoveTarget("project")} /> : <MenuItem label="Remove member" Icon={DeleteIcon} subMenuTrigger="click">
          <MenuItem label="From project" onClick={() => setRemoveTarget("project")} />
          <MenuItem label="From workspace" onClick={() => setRemoveTarget("workspace")} />
        </MenuItem>}

      <Dialog title={`Remove from ${removeTargetDisplayName}`} confirmText="Remove" open={removeTarget !== null} onConfirm={confirmRemove} onCancel={closeDialog} showSpinner={isRemoving}>
        <Text variant="bodyM" sx={{
        marginBottom: SPACE_ELEMENTS_OF_MODAL
      }}>
          When removing {entityLabel} from {removeTargetDisplayName} they will
          no longer be able to access this {removeTargetDisplayName}. You can
          always invite this {entityLabel} again.
        </Text>

        <UserDisplay member={member} />
      </Dialog>
    </>;
}